import React, { useContext } from "react";
import { Handle, Position } from "reactflow";
import { Flex, Tag, Typography } from "antd";
import { PhoneOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { AppContext } from "../../../context/AppContext";
import { conditionCodeToTitle, getConditionIconAndClass } from "../utils/conditionCodeMapping";
import { formatPhoneNumber } from "../../utils/commonUtils";

const { Text } = Typography;

function getServiceIcon(phoneNumber: string, appProperties: { savedNumbers?: Array<{ phoneNumber?: string; integId?: string }>; installedApps?: Array<{ integProps?: { integId?: string }; right?: { service_name?: string } }> } | null): React.ReactNode {
    const saved = appProperties?.savedNumbers?.find((n) => n?.phoneNumber === phoneNumber);
    const serviceName = appProperties?.installedApps?.find((obj) => obj?.integProps?.integId === saved?.integId)?.right?.service_name;
    if (serviceName) {
        return <div className={`hs-scale-08 numberlist-sprites numberlist-${serviceName}`} />;
    }
    return <PhoneOutlined />;
}

const DEFAULT_NODE_ICON = { icon: <div className="automationIconsSprite select-icon" /> };

function getNodeIcon(data: ConditionNodeData) {
    if (!data.conditionCode) return DEFAULT_NODE_ICON;
    const { icon } = getConditionIconAndClass(data.conditionCode);
    return { icon };
}

export interface BusinessHoursDayConfig {
    hourStartTime?: string;
    minuteStartTime?: string;
    hourEndTime?: string;
    minuteEndTime?: string;
    isOpen?: number;
    businessDay?: string;
}

export interface BusinessHoursConfig {
    timeZone?: string;
    businessDays?: BusinessHoursDayConfig[];
}

export interface ConditionNodeData {
    label?: string;
    title?: string;
    conditionCode?: string;
    phoneNumbers?: string[];
    keywords?: string[];
    matchType?: string;
    countries?: string[];
    runSchedule?: string;
    businessHours?: BusinessHoursConfig;
    isPlaceholder?: boolean;
    source?: string;
    deliveryStatus?: string[];
    applyOncePerContact?: boolean;
}

const ConditionNodeComponent: React.FC<{ data: ConditionNodeData & { isOutgoing?: boolean }; selected?: boolean }> = ({ data, selected }) => {
    const [appProperties] = useContext(AppContext);
    const hasTypeSelected = !!data.conditionCode;
    const isPlaceholder = data.isPlaceholder ?? !hasTypeSelected;
    const iconMeta = getNodeIcon(data);

    const displayTitle = isPlaceholder
        ? "Select a Condition..."
        : (data.title ?? conditionCodeToTitle(data.conditionCode) ?? "When to continue");

    return (
        <div
            className={`trigger-rule-node hs-border-grey trigger-rule-node-condition ${isPlaceholder ? "trigger-rule-node-placeholder" : ""} ${selected ? "trigger-rule-node-selected" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={`Condition: ${displayTitle}. ${selected ? "Selected" : "Click to select"}`}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).click();
                }
            }}
        >
            <Handle type="target" position={Position.Top} id="target" className="trigger-rule-node-handle" />
            <Handle type="source" position={Position.Bottom} id="source" className="trigger-rule-node-handle" />
            <span className="trigger-rule-node-tag">Condition</span>

            <div className="trigger-rule-node-header-row d-flex align-items-center gap-2">
                <div className="trigger-rule-node-icon trigger-rule-node-icon-square d-flex align-items-center justify-content-center">
                    {iconMeta.icon}
                </div>
                <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">{displayTitle}</span>
            </div>

            {isPlaceholder ? (
                <span className="trigger-rule-node-desc">Choose from the panel on the right</span>
            ) : (
                <Flex vertical gap="small" justify="space-between" className="py-3">
                    {data.phoneNumbers && data.phoneNumbers.length > 0 && (
                        <div className="trigger-rule-node-detail-row d-flex align-items-center gap-2">
                            <Flex gap="small" align="center" className="trigger-rule-node-detail-value-wrap hs-fs-12 hs-fw-400 hs-color-dark-grey flex-warp">
                                {data.phoneNumbers.map((phoneNumber) => (
                                    <Tag
                                        key={phoneNumber}
                                        icon={getServiceIcon(phoneNumber, appProperties)}
                                        className="trigger-rule-node-detail-tag trigger-rule-node-detail-tag-with-icon d-inline-flex align-items-center"
                                    >
                                        {formatPhoneNumber(phoneNumber)}
                                    </Tag>
                                ))}
                            </Flex>
                        </div>
                    )}
                    {data.keywords && data.keywords.length > 0 && (
                        <div className="trigger-rule-node-detail-row d-flex align-items-center gap-2">
                            <Text className="trigger-rule-node-detail-label">Keywords:</Text>
                            <Flex gap="small" align="center" className="hs-fs-12 hs-fw-400 hs-color-dark-grey flex-warp">
                                {data.keywords.map((kw) => (
                                    <Tag key={kw} className="trigger-rule-node-detail-tag">
                                        {kw}
                                    </Tag>
                                ))}
                            </Flex>
                        </div>
                    )}
                    {data.matchType && (
                        <div className="trigger-rule-node-detail-row d-flex align-items-center gap-2">
                            <Text className="trigger-rule-node-detail-label">Match:</Text>
                            <Flex gap="small" align="center" className="hs-fs-12 hs-fw-400 hs-color-dark-grey flex-warp">
                                <Tag className="trigger-rule-node-detail-tag">
                                    {data.matchType === "StartsWith" ? "Start With" : data.matchType === "Contains" ? "Contains" : data.matchType}
                                </Tag>
                            </Flex>
                        </div>
                    )}
                    {data.countries && data.countries.length > 0 && (
                        <div className="trigger-rule-node-detail-row d-flex align-items-center gap-2">
                            <Text className="trigger-rule-node-detail-label">Countries:</Text>
                            <Flex gap="small" align="center" className="hs-fs-12 hs-fw-400 hs-color-dark-grey flex-warp">
                                {data.countries.map((country) => (
                                    <Tag key={country} className="trigger-rule-node-detail-tag">
                                        {country}
                                    </Tag>
                                ))}
                            </Flex>
                        </div>
                    )}
                    {data.runSchedule && (
                        <div className="trigger-rule-node-detail-row d-flex align-items-center gap-2">
                            <Text className="trigger-rule-node-detail-label">Run schedule:</Text>
                            <Flex gap="small" align="center" className="hs-fs-12 hs-fw-400 hs-color-dark-grey flex-warp">
                                <Tag icon={<ClockCircleOutlined />} className="trigger-rule-node-detail-tag">
                                    {data.runSchedule === "24_7"
                                        ? "24/7"
                                        : data.runSchedule === "BusinessHours"
                                          ? "Business Hours"
                                          : data.runSchedule === "OutsideBusinessHours"
                                            ? "Outside Business Hours"
                                            : data.runSchedule === "SpecificHours"
                                              ? "Specific Hours"
                                              : data.runSchedule}
                                </Tag>
                            </Flex>
                        </div>
                    )}
                </Flex>
            )}
        </div>
    );
};

export const ConditionNode = React.memo(ConditionNodeComponent);
